"use client";
import { useRef, useState } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import type {
  MountainForecast,
  MountainSport,
} from "@/lib/mountain-explorer/types";
import type { MountainSelection } from "@/lib/mountain-explorer/selection";
import { mountainTime } from "@/lib/mountain-explorer/forecast/presentation";
import { readMountainSelection } from "@/lib/mountain-explorer/selection";
import { useHydrated } from "@/lib/hooks/use-minute-clock";
import {
  useShallowSearchParam,
  setShallowSearchParams,
} from "@/lib/hooks/use-shallow-search-param";
const MountainExplorerDialog = dynamic(
  () => import("./MountainExplorerDialog"),
  { ssr: false },
);
export type MountainExplorerProps = {
  forecast: MountainForecast;
  selection: MountainSelection;
  sport: MountainSport;
  now: number;
  onSelection(value: MountainSelection): void;
  onSport(sport: MountainSport): void;
};
export default function MountainExplorerEntry(props: MountainExplorerProps) {
  const hydrated = useHydrated();
  const [open, setOpen] = useState(false),
    button = useRef<HTMLButtonElement>(null);
  const date = useShallowSearchParam("forecastDate"),
    hour = useShallowSearchParam("forecastHour");
  return (
    <section
      className="mt-5 overflow-hidden rounded-[18px] border border-border bg-navy text-white"
      aria-label="Explore Zermatt"
    >
      <div className="flex flex-col sm:flex-row">
        <div className="relative h-32 shrink-0 sm:h-auto sm:w-44">
          <Image
            src="/images/locations/zermatt.jpg"
            alt="The Matterhorn above Zermatt"
            fill
            sizes="(max-width: 640px) 100vw, 176px"
            className="object-cover"
          />
        </div>
        <div className="flex flex-1 flex-wrap items-center justify-between gap-4 p-5">
          <div>
            <h3 className="font-heading text-xl font-bold">
              Step into Zermatt
            </h3>
            <p className="mt-1 max-w-md text-sm text-white/75">
              Explore Riffelberg and Gornergrat, with the Matterhorn on the
              horizon.
            </p>
            <p className="mt-2 text-xs text-white/65">
              Forecast simulation · {props.selection.date} ·{" "}
              {String(props.selection.hour).padStart(2, "0")}:00
            </p>
          </div>
          <button
            ref={button}
            type="button"
            disabled={!hydrated}
            className="min-h-12 rounded-xl bg-white px-5 py-3 text-sm font-bold text-navy focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-light"
            onClick={() => {
              const current = mountainTime(Date.now());
              props.onSelection(
                readMountainSelection(date, hour, current.date, current.hour),
              );
              setOpen(true);
            }}
          >
            Enter the mountain <span aria-hidden="true">↗</span>
          </button>
        </div>
      </div>
      {open && (
        <MountainExplorerDialog
          {...props}
          onClose={() => {
            setOpen(false);
            setShallowSearchParams({ mountainView: null });
            requestAnimationFrame(() => button.current?.focus());
          }}
        />
      )}
    </section>
  );
}
